import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import {
  FormControlLabel, FormGroup, IconButton, keyframes, Switch,
} from '@mui/material';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { ensureSafariAudioSession, isSafari } from '../../../utils/platform';
import AudioRecorder from '../../molecules/AudioRecorder/AudioRecorder';
import Metronome from '../../molecules/Metronome/Metronome';
import MetronomeControls from '../../molecules/Metronome/MetronomeControls';
import InputDeviceSelector from './InputDeviceSelector';
import useCountIn from './useCountIn';
import useOnDocumentHidden from '../../../hooks/useOnDocumentHidden';

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0.3;
  }
`;

const useStyles = makeStyles()((theme) => ({
  songRecorder: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    alignItems: 'center',
    width: '100%',
  },
  metronome: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    alignItems: 'center',
  },
  inputDeviceSelect: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    minWidth: '20ch',
    maxWidth: '40ch',
  },
  recordingContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      justifyContent: 'center',
    },
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 3,
  },
  countIn: {
    fontSize: '3rem',
    fontWeight: 'bold',
    animation: `${fadeOut} 2s ${theme.transitions.easing.easeInOut}`,
    animationIterationCount: 1,
    animationFillMode: 'forwards',
  },
  inputControls: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'center',
  },
  metronomeControls: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    alignItems: 'center',
  },
  warning: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.warning.main,
  },
}));

export interface SongRecorderProps {
  onRecordingComplete?: (
    audioBlob: Blob,
    bpm?: number,
    timeSig?: number,
    startTimeOffset?: number
  ) => void;
  onMinDurationReached?: () => void;
  onMaxDurationReached?: () => void;
  allowRecording?: boolean;
  minDuration?: number;
  maxDuration?: number;
}

const SongRecorder: React.FC<SongRecorderProps> = ({
  onRecordingComplete,
  onMaxDurationReached,
  onMinDurationReached,
  minDuration = 5,
  maxDuration = 300,
  allowRecording = true,
}) => {
  const { classes } = useStyles();

  const firstBeatTime = useRef<number>(-1);
  const isFirstBeat = useRef<boolean>(true);
  const goTimeRef = useRef<number>(-1);

  const [useMetronome, setUseMetronome] = useState(false);
  const [metronomeOn, setMetronomeOn] = useState(false);
  const [bpm, setBpm] = useState(120);
  const [timeSig, setTimeSig] = useState(4);

  const { t } = useTranslation();

  const [recording, setRecording] = useState(false);
  const [recordAudio, setRecordAudio] = useState(false);

  const [inputDevice, setInputDevice] = useState('');

  const [cantAcquireUserMedia, setCantAcquireUserMedia] = useState(false);

  const {
    count: countIn,
    start: startCountIn,
    cancel: cancelCountIn,
    isActive: isCountInActive,
  } = useCountIn();

  const beginCountIn = useCallback(() => {
    const intervalMs = useMetronome ? (1000 * timeSig) / (bpm / 60) : 1000;
    setMetronomeOn(false);
    goTimeRef.current = -1;
    startCountIn({ intervalMs, startCount: 4 });
  }, [bpm, timeSig, useMetronome, startCountIn]);

  const toggleRecording = useCallback(() => {
    if (!recording) {
      // get audio device permissions, then start count in
      ensureSafariAudioSession();
      const constraints = {
        audio: inputDevice ? { deviceId: { exact: inputDevice } } : true,
      };
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then(() => {
          beginCountIn();
          setCantAcquireUserMedia(false);
        })
        .catch((e) => {
          setRecording(false);
          setCantAcquireUserMedia(true);
          console.error('Error acquiring audio media:', e);
        });

      setRecording(true);
    } else {
      setRecording(false);
      setRecordAudio(false);
      cancelCountIn();
    }
  }, [recording, beginCountIn, cancelCountIn, inputDevice]);

  const toggleUseMetronome = useCallback(() => {
    const newUseMetronome = !useMetronome;

    // don't measure start time if metronome was enabled during recording
    if (newUseMetronome && recordAudio) {
      isFirstBeat.current = false;
    }

    setUseMetronome(newUseMetronome);
    setMetronomeOn(newUseMetronome);
  }, [useMetronome, recordAudio]);

  // Stop metronome when recording finished
  useEffect(() => {
    if (!recording) {
      setMetronomeOn(false);
    }
  }, [recording]);

  useEffect(() => {
    // start metronome to sync up with count in
    if (countIn === 3 && useMetronome) {
      setMetronomeOn(true);
    }
    // start recording audio early
    if (countIn === 1) {
      setRecordAudio(true);
    }
    if (countIn === 0) {
      isFirstBeat.current = true;
      goTimeRef.current = performance.now();
    }
  }, [countIn, useMetronome]);

  // stop recording / count in when not allowed
  useEffect(() => {
    if (!allowRecording) {
      if (recordAudio) {
        setRecordAudio(false);
        setRecording(false);
      }
      if (!recordAudio && recording) {
        setRecording(false);
        cancelCountIn();
      }
    }
  }, [allowRecording, recordAudio, recording, cancelCountIn]);

  const handleMinDurationReached = useCallback(() => {
    if (onMinDurationReached) {
      onMinDurationReached();
    }
  }, [onMinDurationReached]);

  const handleMaxDurationReached = useCallback(() => {
    if (recording) {
      toggleRecording();
    }
    if (onMaxDurationReached) {
      onMaxDurationReached();
    }
  }, [onMaxDurationReached, recording, toggleRecording]);

  const handleMetronomeTick = useCallback((beatNo: number, offset: number) => {
    // record time of first metronome beat
    if (isFirstBeat.current && beatNo === 0) {
      isFirstBeat.current = false;
      firstBeatTime.current = performance.now() - offset;
    }
  }, []);

  const handleRecordingComplete = useCallback(
    (audioBlob: Blob, recordingStartTime?: number) => {
      if (!onRecordingComplete) return;

      let startTimeOffset: number | undefined;

      if (useMetronome) {
        // calculate difference between first beat and recording start time
        if (typeof recordingStartTime === 'number' && firstBeatTime.current >= 0) {
          startTimeOffset = firstBeatTime.current - recordingStartTime;
          onRecordingComplete(audioBlob, bpm, timeSig, startTimeOffset);
        }
      } else if (typeof recordingStartTime === 'number' && goTimeRef.current >= 0) {
        // if no metronome was used, calculate offset from count in "go time"
        startTimeOffset = goTimeRef.current - recordingStartTime;
        onRecordingComplete(audioBlob, undefined, undefined, startTimeOffset);
      } else {
        onRecordingComplete(audioBlob);
      }
    },
    [onRecordingComplete, useMetronome, bpm, timeSig],
  );

  // stop recording when document is hidden
  useOnDocumentHidden(() => {
    if (isCountInActive) {
      cancelCountIn();
      setRecordAudio(false);
      setRecording(false);
      setMetronomeOn(false);
      return;
    }
    if (recordAudio || recording) {
      setRecordAudio(false);
      setRecording(false);
    }
  });

  return (
    <div className={classes.songRecorder}>
      <div className={classes.metronomeControls}>
        <FormGroup>
          <FormControlLabel
            control={(
              <Switch
                id={useMetronome ? 'btn_recording_metronome_off' : 'btn_recording_metronome_on'}
                checked={useMetronome}
                onChange={toggleUseMetronome}
              />
            )}
            label={t('song_recorder.metronome')}
          />
        </FormGroup>

        {useMetronome && (
          <div className={classes.metronome}>
            <MetronomeControls
              onBpmChange={setBpm}
              onTimeSigChange={setTimeSig}
              bpm={bpm}
              timeSig={timeSig}
              isOn={metronomeOn}
            />
            <Metronome
              bpm={bpm}
              timeSig={timeSig}
              isOn={metronomeOn}
              onTick={handleMetronomeTick}
            />
          </div>
        )}
      </div>

      <div className={classes.inputControls}>
        <div className={classes.inputDeviceSelect}>
          <InputDeviceSelector onDeviceSelect={setInputDevice} disabled={recording} />
        </div>
      </div>

      <div className={classes.recordingContainer}>
        <IconButton
          onClick={toggleRecording}
          color="primary"
          size="large"
          id={recording ? 'btn_recording_stop' : 'btn_recording_start'}
          disabled={!allowRecording}
        >
          {recording ? (
            <StopIcon fontSize="large" />
          ) : (
            <FiberManualRecordIcon color="error" fontSize="large" />
          )}
        </IconButton>

        {countIn >= 0 && (
          <div className={classes.overlay}>
            <span key={countIn} className={classes.countIn}>
              {countIn === 0 ? t('song_recorder.go') : countIn}
            </span>
          </div>
        )}

        <AudioRecorder
          recording={recordAudio}
          onMaxDurationReached={handleMaxDurationReached}
          onMinDurationReached={handleMinDurationReached}
          minDuration={minDuration}
          maxDuration={maxDuration}
          inputDeviceId={inputDevice}
          mimeType={isSafari() ? 'audio/mp4' : 'audio/webm;codecs=opus'}
          onRecordingComplete={handleRecordingComplete}
        />
      </div>
      {cantAcquireUserMedia && (
        <span className={classes.warning}>{t('song_recorder.cant_aquire_user_media')}</span>
      )}
    </div>
  );
};

export default SongRecorder;
